import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import { 
  Home, Code2, BookOpen, Image as ImageIcon, 
  Mail, LogOut, LogIn, Menu, X, LayoutDashboard 
} from "lucide-react";
import BreadCrumb from "../Breadcrumb/breadcrumb";

// Inline brand SVGs since Lucide no longer includes brands
const GithubIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M15 22v-4a4.8 4.8 0 0 0-1-3.2c3 0 6-2 6-5.5.08-1.25-.27-2.48-1-3.5.28-1.15.28-2.35 0-3.5 0 0-1 0-3 1.5-2.64-.5-5.36-.5-8 0C6 2 5 2 5 2c-.3 1.15-.3 2.35 0 3.5A5.403 5.403 0 0 0 4 9c0 3.5 3 5.5 6 5.5-.39.49-.68 1.05-.85 1.65-.17.6-.22 1.23-.15 1.85v4" />
    <path d="M9 18c-4.51 2-5-2-7-2" />
  </svg>
);

const LinkedinIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
    <rect width="4" height="12" x="2" y="9" />
    <circle cx="4" cy="4" r="2" />
  </svg>
);

const Nav = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const isLoggedIn = !!(Cookies.get("accessToken") && Cookies.get("refreshToken"));
  const profileName = Cookies.get("profileName") ? decodeURIComponent(Cookies.get("profileName")).split(" ")[0] : "";

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    Cookies.remove("accessToken");
    Cookies.remove("refreshToken");
    Cookies.remove("profileName");
    Cookies.remove("profileImage");
    navigate("/login");
  };

  const navItems = [
    { name: "Home", path: "/", icon: Home, show: true },
    { name: "Problems", path: "/problems", icon: Code2, show: true },
    { name: "DSA Sheets", path: "/dsasheet?name=striver", icon: LayoutDashboard, show: true },
    { name: "Notes", path: "/notes", icon: BookOpen, show: isLoggedIn },
    { name: "Images", path: "/images", icon: ImageIcon, show: isLoggedIn },
    { name: "Contact", path: "/contact", icon: Mail, show: true },
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar - Desktop */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transition-transform duration-300 ease-in-out hidden md:flex flex-col ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="h-16 flex items-center px-6 border-b border-slate-200">
          <img src="/trackdsa-logo.png" alt="TrackDSA" className="h-8" />
        </div>
        
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {navItems.filter(item => item.show).map((item) => {
            const isActive = location.pathname === item.path || (location.pathname === "/dsasheet" && item.name === "DSA Sheets");
            return (
              <Link
                key={item.name}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive 
                    ? "bg-blue-50 text-blue-700" 
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`}
              >
                <item.icon className={`w-5 h-5 ${isActive ? "text-blue-700" : "text-slate-400"}`} />
                {item.name}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-200">
          {isLoggedIn ? (
            <button onClick={handleLogout} className="flex items-center gap-3 w-full px-3 py-2.5 text-sm font-medium text-red-600 rounded-lg hover:bg-red-50 transition-colors">
              <LogOut className="w-5 h-5" />
              Logout
            </button>
          ) : (
            <Link to="/login" className="flex items-center gap-3 w-full px-3 py-2.5 text-sm font-medium text-blue-600 rounded-lg hover:bg-blue-50 transition-colors">
              <LogIn className="w-5 h-5" />
              Login
            </Link>
          )}
        </div>
      </aside>

      {/* Main Content Wrapper */}
      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${isSidebarOpen ? "md:ml-64" : ""}`}>
        {/* Top Navbar */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 sm:px-6 z-40 sticky top-0">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="hidden md:block p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <Menu className="w-5 h-5" />
            </button>
            <button 
              onClick={() => setIsMobileMenuOpen(true)}
              className="md:hidden p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>

          <div className="flex items-center gap-4">
            <a href="https://github.com/Maayur28" target="_blank" rel="noreferrer" className="text-slate-400 hover:text-slate-900 transition-colors">
              <GithubIcon className="w-5 h-5" />
            </a>
            <a href="https://www.linkedin.com/in/mayur28/" target="_blank" rel="noreferrer" className="text-slate-400 hover:text-blue-600 transition-colors">
              <LinkedinIcon className="w-5 h-5" />
            </a>
            <div className="h-6 w-px bg-slate-200 mx-2"></div>
            {isLoggedIn ? (
              <Link to="/account" className="flex items-center gap-2 hover:bg-slate-50 py-1 px-2 rounded-lg transition-colors">
                <span className="text-sm font-medium text-slate-700 hidden sm:block">Hello, {profileName}</span>
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold">
                  {profileName.charAt(0).toUpperCase()}
                </div>
              </Link>
            ) : (
              <Link to="/login" className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors">Sign in</Link>
            )}
          </div>
        </header>

        {/* Page Content goes here */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
          <BreadCrumb />
          {children}
        </main>
      </div>

      {/* Mobile Sidebar Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)} />
          <div className="fixed inset-y-0 left-0 w-64 bg-white shadow-xl flex flex-col">
            <div className="h-16 flex items-center justify-between px-6 border-b border-slate-200">
              <img src="/trackdsa-logo.png" alt="TrackDSA" className="h-8" />
              <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
              {navItems.filter(item => item.show).map((item) => {
                const isActive = location.pathname === item.path || (location.pathname === "/dsasheet" && item.name === "DSA Sheets");
                return (
                  <Link
                    key={item.name}
                    to={item.path}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      isActive 
                        ? "bg-blue-50 text-blue-700" 
                        : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                    }`}
                  >
                    <item.icon className={`w-5 h-5 ${isActive ? "text-blue-700" : "text-slate-400"}`} />
                    {item.name}
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>
      )}
    </div>
  );
};

export default Nav;